import { BackendError } from '@oresundsbron/api';
import { Grid } from '@oresundsbron/bridge-ui';
import { FormStoreProvider } from '@oresundsbron/use-form';
import Script from 'next/script';
import { FC, useEffect, useMemo } from 'react';
import { CreditEvalError, isCreditError } from '../../../lib/errors';
import { CreditCardPayment } from '../CreditCardPayment';
import Error from '../Error';
import { SetPassword } from '../SetPassword';
import { resetStore, useSignUp } from '../stores/useSignUp';
import { ThankYou } from '../ThankYou';
import { CreditEvaluationError } from './CreditEvaluationError';
import SignUpForm from './Form';
import { reset } from '~/stores/contracts';
import CommercialVehicleError from '../CommercialVehicleError';
import { AddRecognitionError } from '@oresundsbron/validators/src/constants';
import { useRouter } from 'next/router';
import { useBannerVisibility } from '@oresundsbron/app-common';

const renderError = (e: BackendError) => {
  if (isCreditError(e.error)) {
    return <CreditEvaluationError status={e.error as CreditEvalError} />;
  } else if (e.error === AddRecognitionError.blockCommercialVehicle) {
    return <CommercialVehicleError />;
  } else {
    return <Error />;
  }
};

export const SignUp: FC<{ commuter?: boolean }> = ({ commuter }) => {
  const { error, resp, isPasswordSet, payWithCreditCard } = useSignUp();
  const { query } = useRouter();
  const isBankIDResult = query.complete === '1';
  const { setBannerVisible } = useBannerVisibility();
  const content = useMemo(() => {
    if (isBankIDResult) {
      setBannerVisible(false);
      return <ThankYou ns="privateSignUp" />;
    }

    if (error) {
      return renderError(error);
    } else if (!!payWithCreditCard && !resp) {
      return <CreditCardPayment />;
    } else if (!!resp) {
      if (isPasswordSet) {
        return <ThankYou ns="businessSignUp" />;
      } else {
        return <SetPassword ns="businessSignUp" />;
      }
    } else {
      setBannerVisible(false);
      return <SignUpForm commuter={commuter} />;
    }
  }, [
    isBankIDResult,
    error,
    payWithCreditCard,
    resp,
    setBannerVisible,
    isPasswordSet,
    commuter,
  ]);

  useEffect(
    () => () => {
      resetStore();
      reset();
    },
    []
  );

  return (
    <>
      <FormStoreProvider>
        <Grid>{content}</Grid>
      </FormStoreProvider>
      <Script
        strategy="lazyOnload"
        src="https://checkout.reepay.com/checkout.js"
      />
    </>
  );
};

export default SignUp;
